/** Page size for Inbox "Your uploads" recent + archive views. */
export const INBOX_UPLOADS_PAGE_SIZE = 15;

export const INBOX_UPLOAD_JOB_TYPES = [
  "application_enrichment",
  "entity_refine",
  "knowledge_ingestion",
];

export function isInboxUploadJobType(job) {
  const t = String(job?.job_type || job || "").toLowerCase();
  return INBOX_UPLOAD_JOB_TYPES.includes(t);
}

export function isKnowledgeIngestionJob(job) {
  return String(job?.job_type || "").toLowerCase() === "knowledge_ingestion";
}

/**
 * @typedef {'recent' | '7d' | '30d' | 'older'} InboxUploadsTimeRange
 */

/**
 * Build created_after / created_before ISO bounds for an archive time window.
 * @param {InboxUploadsTimeRange} timeRange
 * @param {Date} [now]
 * @returns {{ created_after: string|null, created_before: string|null } | null}
 */
export function inboxUploadsDateBounds(timeRange, now = new Date()) {
  const ms = now.getTime();
  if (!Number.isFinite(ms)) return null;
  const iso = (d) => d.toISOString();
  if (timeRange === "7d") {
    return {
      created_after: iso(new Date(ms - 7 * 24 * 60 * 60 * 1000)),
      created_before: null,
    };
  }
  if (timeRange === "30d") {
    return {
      created_after: iso(new Date(ms - 30 * 24 * 60 * 60 * 1000)),
      created_before: null,
    };
  }
  if (timeRange === "older") {
    return {
      created_after: null,
      created_before: iso(new Date(ms - 30 * 24 * 60 * 60 * 1000)),
    };
  }
  return null;
}

/**
 * Status values that map 1:1 to the jobs API `status` query param.
 * Multi-status chips stay client-side only.
 * @param {string} jobFilter
 * @returns {string|null}
 */
export function inboxUploadsApiStatus(jobFilter) {
  const key = String(jobFilter || "").toLowerCase();
  if (key === "completed" || key === "failed") return key;
  return null;
}

/**
 * Narrow a window's created_before to page deeper using the oldest row already shown.
 * @param {{ created_after: string|null, created_before: string|null }} bounds
 * @param {string|null|undefined} oldestCreatedAt
 */
export function inboxUploadsBoundsForOlderPage(bounds, oldestCreatedAt) {
  const base = {
    created_after: bounds?.created_after || null,
    created_before: bounds?.created_before || null,
  };
  const cursor = String(oldestCreatedAt || "").trim();
  if (!cursor) return base;
  if (!base.created_before || cursor < base.created_before) {
    base.created_before = cursor;
  }
  return base;
}

/**
 * @param {object} opts
 * @param {string} opts.jobType
 * @param {number} [opts.limit]
 * @param {number} [opts.offset]
 * @param {string|null} [opts.created_after]
 * @param {string|null} [opts.created_before]
 * @param {string|null} [opts.status]
 */
export function buildInboxUploadsJobsUrl({
  jobType,
  limit = INBOX_UPLOADS_PAGE_SIZE,
  offset = 0,
  created_after = null,
  created_before = null,
  status = null,
}) {
  const params = new URLSearchParams();
  params.set("limit", String(Math.max(1, Math.min(50, limit))));
  params.set("offset", String(Math.max(0, offset)));
  if (jobType) params.set("job_type", jobType);
  if (created_after) params.set("created_after", created_after);
  if (created_before) params.set("created_before", created_before);
  if (status) params.set("status", status);
  return `/api/jobs?${params.toString()}`;
}

export function sortJobsByCreatedDesc(jobs) {
  return [...(jobs || [])].sort((a, b) => {
    const at = new Date(a?.created_at || a?.updated_at || 0).getTime();
    const bt = new Date(b?.created_at || b?.updated_at || 0).getTime();
    return bt - at;
  });
}

/**
 * Merge typed job list responses into one page (newest first).
 * @param {Array<{ jobs?: any[], total?: number, has_more?: boolean }>} responses
 * @param {number} [pageSize]
 * @param {Set<string>} [excludeIds] already-shown ids when loading more
 */
export function mergeInboxUploadPages(
  responses,
  pageSize = INBOX_UPLOADS_PAGE_SIZE,
  excludeIds = null
) {
  const lists = Array.isArray(responses) ? responses : [];
  const byId = new Map();
  let total = 0;
  let anyHasMore = false;
  for (const res of lists) {
    total += Number(res?.total || 0) || 0;
    if (res?.has_more) anyHasMore = true;
    for (const job of res?.jobs || []) {
      const id = String(job?.id || "").trim();
      if (!id || byId.has(id)) continue;
      if (excludeIds && excludeIds.has(id)) continue;
      byId.set(id, job);
    }
  }
  const merged = sortJobsByCreatedDesc([...byId.values()]);
  const size = Math.max(1, pageSize);
  const page = merged.slice(0, size);
  const shown = page.length;
  const has_more =
    shown > 0
      ? anyHasMore || merged.length > size || shown < total
      : total > 0 && anyHasMore;
  // When excludeIds is set (load more), total is still window total; has_more if we got a full page
  // or server indicated more within the narrowed cursor.
  const loadMoreAwareHasMore =
    excludeIds && excludeIds.size > 0
      ? page.length >= size || anyHasMore || merged.length > size
      : Boolean(has_more && (page.length < total || anyHasMore || merged.length > size));

  return {
    jobs: page,
    total,
    has_more: Boolean(loadMoreAwareHasMore),
  };
}

export function inboxUploadsJobTypeUrls(
  bounds,
  { pageSize = INBOX_UPLOADS_PAGE_SIZE, status = null, offset = 0 } = {}
) {
  return INBOX_UPLOAD_JOB_TYPES.map((jobType) =>
    buildInboxUploadsJobsUrl({
      jobType,
      limit: pageSize,
      offset,
      created_after: bounds?.created_after || null,
      created_before: bounds?.created_before || null,
      status,
    })
  );
}
